import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Card, { CardHeader, CardTitle } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import {
  QueueListIcon,
  ShoppingCartIcon,
  RectangleStackIcon,
  CpuChipIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const statusVariant = {
  PENDING_SLICING: 'warning',
  READY_TO_PRINT: 'primary',
  PRINTING: 'primary',
  COMPLETED: 'success',
  FAILED: 'danger',
  ON_HOLD: 'default',
  CANCELLED: 'default',
};

export default function Dashboard() {
  const [stats, setStats] = useState({ orders: 0, queueJobs: 0, slicerJobs: 0, pendingQuotes: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const [queueRes, ordersRes, jobsRes, quotesRes] = await Promise.allSettled([
          api.get('/auth/print-queue/dashboard'),
          api.get('/auth/payments/orders', { params: { limit: 5 } }),
          api.get('/auth/slicer-job', { params: { limit: 5 } }),
          api.get('/auth/slicer-quote/board'),
        ]);

        const queueData = queueRes.status === 'fulfilled' ? queueRes.value.data : {};
        const ordersData = ordersRes.status === 'fulfilled' ? ordersRes.value.data : [];
        const jobsData = jobsRes.status === 'fulfilled' ? jobsRes.value.data : [];
        const quotesData = quotesRes.status === 'fulfilled' ? quotesRes.value.data : {};

        const ordersList = Array.isArray(ordersData) ? ordersData : ordersData?.orders || [];
        const jobsList = Array.isArray(jobsData) ? jobsData : jobsData?.jobs || [];

        const pendingQuotes = quotesData?.columns
          ? Object.values(quotesData.columns).reduce((sum, col) => sum + (col?.items?.length || 0), 0)
          : 0;

        setStats({
          orders: ordersList.length,
          queueJobs: queueData?.jobs?.length || queueData?.length || 0,
          slicerJobs: jobsList.length,
          pendingQuotes,
        });

        setRecentOrders(ordersList.slice(0, 5));
        setRecentJobs(jobsList.slice(0, 5));
      } catch {
        // dashboard stats are non-critical
      } finally {
        setLoading(false);
      }
    }
    fetchDashboard();
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" description="Overview of your 3D printing operations" />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Orders" value={loading ? '—' : stats.orders} icon={ShoppingCartIcon} variant="primary" />
        <StatCard label="Print Queue" value={loading ? '—' : stats.queueJobs} icon={QueueListIcon} variant="warning" />
        <StatCard label="Slicer Jobs" value={loading ? '—' : stats.slicerJobs} icon={CpuChipIcon} variant="success" />
        <StatCard label="Slicer Quotes" value={loading ? '—' : stats.pendingQuotes} icon={RectangleStackIcon} variant="danger" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { to: '/orders', label: 'View Orders', icon: ShoppingCartIcon, color: 'blue' },
          { to: '/print-queue', label: 'Print Queue', icon: QueueListIcon, color: 'amber' },
          { to: '/slicer-quotes', label: 'Slicer Quotes', icon: RectangleStackIcon, color: 'green' },
        ].map(({ to, label, icon: Icon, color }) => (
          <Link key={to} to={to} className="group">
            <Card hover className="flex items-center gap-4 !p-4">
              <div className={`p-2 bg-${color}-100 rounded-lg`}>
                <Icon className={`h-5 w-5 text-${color}-600`} />
              </div>
              <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">{label}</span>
              <ArrowRightIcon className="h-4 w-4 text-slate-400 ml-auto group-hover:text-slate-600 transition-colors" />
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <Link to="/orders" className="text-sm text-blue-600 hover:text-blue-700 font-medium">View all</Link>
          </CardHeader>
          {recentOrders.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No recent orders</p>
          ) : (
            <div className="divide-y divide-slate-100 -mx-6">
              {recentOrders.map((order, i) => (
                <Link key={order._id || i} to={`/orders/${order._id}`} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-slate-900">#{order.orderNumber || order._id?.slice(-6)}</p>
                    <p className="text-xs text-slate-500">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}</p>
                  </div>
                  <Badge variant={order.status === 'PAID' ? 'success' : order.status === 'CANCELLED' ? 'danger' : 'default'} size="sm">
                    {order.status || 'N/A'}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Slicer Jobs</CardTitle>
            <Link to="/slicer-jobs" className="text-sm text-blue-600 hover:text-blue-700 font-medium">View all</Link>
          </CardHeader>
          {recentJobs.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No recent jobs</p>
          ) : (
            <div className="divide-y divide-slate-100 -mx-6">
              {recentJobs.map((job, i) => (
                <Link key={job._id || i} to={`/slicer-jobs/${job._id}`} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{job.name || job._id?.slice(-8)}</p>
                    <p className="text-xs text-slate-500">{job.createdAt ? new Date(job.createdAt).toLocaleDateString() : ''}</p>
                  </div>
                  <Badge variant={statusVariant[job.status] || 'default'} size="sm">
                    {job.status || 'N/A'}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
